import { z } from "zod";
import { StateMachine } from "../utils/state-machine.utils.js";
import { StatefulValue } from "../utils/stateful-value.utils.js";
import { InvalidOpenInvitationStateError } from "../errors/open-invitation-state.error.js";

export const OpenInvitationStatusSchema = z.enum({
  ACTIVE: "ACTIVE",
  DISABLED: "DISABLED",
  EXPIRED: "EXPIRED",
  EXHAUSTED: "EXHAUSTED",
});

export type OpenInvitationStatusType = z.infer<
  typeof OpenInvitationStatusSchema
>;

export class OpenInvitationState extends StatefulValue<OpenInvitationStatusType> {
  static readonly values = OpenInvitationStatusSchema.enum;

  private static readonly TERMINAL_STATUSES: readonly OpenInvitationStatusType[] =
    ["EXPIRED", "EXHAUSTED"] as const;

  private static readonly TRANSITIONS_DATA: ReadonlyArray<
    readonly [OpenInvitationStatusType, readonly OpenInvitationStatusType[]]
  > = [
    ["ACTIVE", ["DISABLED", "EXPIRED", "EXHAUSTED"]],
    ["DISABLED", ["ACTIVE"]], // Can re-enable
    ["EXPIRED", []], // Terminal
    ["EXHAUSTED", []], // Terminal
  ];

  private static readonly stateMachine = new StateMachine(
    OpenInvitationState.TRANSITIONS_DATA,
    OpenInvitationState.TERMINAL_STATUSES,
  );

  static readonly ACTIVE = new OpenInvitationState(
    OpenInvitationState.values.ACTIVE,
  );
  static readonly DISABLED = new OpenInvitationState(
    OpenInvitationState.values.DISABLED,
  );
  static readonly EXPIRED = new OpenInvitationState(
    OpenInvitationState.values.EXPIRED,
  );
  static readonly EXHAUSTED = new OpenInvitationState(
    OpenInvitationState.values.EXHAUSTED,
  );

  private constructor(protected readonly value: OpenInvitationStatusType) {
    super(OpenInvitationState.stateMachine, value);
  }

  static fromString(value: string): OpenInvitationState {
    return StatefulValue.lookupOrThrow(
      OpenInvitationState,
      value,
      (v) => new InvalidOpenInvitationStateError(v),
    );
  }
}
